import { cloneDeep } from 'lodash-es';
import { globalStores, TGlobalStores } from '../stores';
import { modelManager } from '../manager/entityManager';
import { EEntityType, EOperateMode, EToolType } from '../types';
import { EOperationType } from '../types/operation';
import type { IFurniture } from '@/editor/data/model/furniture';
import { IPoint2d } from '../math/IPoint2d';
import { getUniqueId } from '../utils';

export class FurnitureOperation {
  private _modelStore: TGlobalStores['modelStore'];
  private _history: TGlobalStores['history'];

  constructor(gs: TGlobalStores) {
    this._modelStore = gs.modelStore;
    this._history = gs.history;
  }

  private $history_addFurniture(furnitureData: IFurniture) {
    this._history.startOperation();
    this._modelStore.addFurnitureData(furnitureData);
    this._history.completeOperation(EOperationType.FurnitureAdd);
  }

  private $history_deleteFurniture(furnitureId: string) {
    this._history.startOperation();
    this._modelStore.removeFurnitureData(furnitureId);
    this._history.completeOperation(EOperationType.FurnitureDel);
  }

  private $history_modifyFurniture(furnitureId: string, furnitureData: Partial<IFurniture>) {
    this._history.startOperation();
    this._modelStore.updateFurnitureData(furnitureId, furnitureData);
    this._history.completeOperation(EOperationType.FurnitureModify);
  }

  addFurniture(furnitureData: IFurniture) {
    this.$history_addFurniture(furnitureData);
    modelManager.furnitureManager.updateEntity(furnitureData);
  }

  generateFurnitureArray(
    furnitureData: IFurniture,
    gridData: { countX: number; countY: number; stepX: IPoint2d; stepY: IPoint2d }
  ) {
    this._history.startOperation();
    for (let i = 0; i < gridData.countX; i++) {
      for (let j = 0; j < gridData.countY; j++) {
        if (i === 0 && j === 0) {
          // 阵列的第一个家具就是原家具
          continue;
        }
        const newFurnitureData = cloneDeep(furnitureData);
        newFurnitureData.id = getUniqueId();
        newFurnitureData.center = {
          x: furnitureData.center.x + gridData.stepX.x * i + gridData.stepY.x * j,
          y: furnitureData.center.y + gridData.stepX.y * i + gridData.stepY.y * j
        };
        this._modelStore.addFurnitureData(newFurnitureData);
      }
    }
    this._history.completeOperation(EOperationType.FurnitureAdd);
    modelManager.furnitureManager.update();
  }

  modifyFurniture(furnitureId: string, furnitureData: Partial<IFurniture>) {
    this.$history_modifyFurniture(furnitureId, furnitureData);
    modelManager.furnitureManager.updateEntity({ id: furnitureId });
  }

  replaceFurnituresModel(furnitureIds: string[], modelId: string) {
    const modelData = this._modelStore.getModel(modelId);
    if (!modelData) return false;

    this._history.startOperation();
    for (const id of furnitureIds) {
      const furnitureData = this._modelStore.getFurnitureData(id);
      if (!furnitureData) {
        continue;
      }
      const nextData = cloneDeep(furnitureData);
      nextData.modelId = modelId;
      nextData.width = modelData.properties.width;
      nextData.height = modelData.properties.height;
      nextData.thickness = modelData.properties.thickness;
      this._modelStore.updateFurnitureData(id, nextData);
    }
    this._history.completeOperation(EOperationType.FurnitureModify);

    modelManager.furnitureManager.update();
    modelManager.selectedRerender();
    return true;
  }

  replaceFurnitureModel(furnitureId: string, modelId: string) {
    const furnitureData = this._modelStore.getFurnitureData(furnitureId);
    const modelData = this._modelStore.getModel(modelId);

    if (!furnitureData || !modelData) return;

    const nextData = cloneDeep(furnitureData);
    nextData.modelId = modelId;
    nextData.width = modelData.properties.width;
    nextData.height = modelData.properties.height;
    nextData.thickness = modelData.properties.thickness;
    this.$history_modifyFurniture(furnitureId, nextData);
    modelManager.furnitureManager.updateEntity({ id: furnitureId });
    modelManager.selectedRerender();
    return true;
  }

  deleteFurniture(furnitureId: string) {
    const furnitureData = this._modelStore.getFurnitureData(furnitureId);
    if (!furnitureData) return;

    // 组合内的家具无法被单独选中，因此这里删的家具一定不是组合成员，不需要联动组合。
    // 会牵动组合的家具删除（清空全部家具、清空房间/分区）在 CompositeOperation 与 RoomOperation 里
    this.$history_deleteFurniture(furnitureId);

    modelManager.furnitureManager.updateEntity({ id: furnitureId });
    modelManager.selectedRerender();
    return true;
  }

  copyFurniture(furnitureId: string) {
    const furnitureData = this._modelStore.getFurnitureData(furnitureId);
    if (!furnitureData) return;

    const copyFurnitureData = cloneDeep(furnitureData);

    globalStores.editorStateStore.furnitureVirtualData = {
      ...copyFurnitureData,
      id: ''
    };

    globalStores.operateInfo = {
      mode: EOperateMode.DrawFurniture,
      toolType: EToolType.model,

      extraState: {
        copying: true,
        furnitureData: {
          modelId: copyFurnitureData.modelId
        }
      }
    };

    globalStores.editorStateStore.setSelected({
      entityId: '',
      type: EEntityType.EMPTY
    });
  }
}
